// src/components/workflows/views/StatusCard.jsx - Status overview for automation dashboard
import React from 'react';
import { Activity, Clock, CheckCircle2, XCircle, Loader2, Pause } from 'lucide-react';

/**
 * StatusCard - Shows automation status overview
 *
 * Props:
 *   lastRun: { triggered_at, status, completed_at } | null
 *   nextRun: string (ISO timestamp) | null
 *   isScheduleEnabled: boolean
 */
const StatusCard = ({ lastRun, nextRun, isScheduleEnabled }) => {
  // Determine current status
  const getStatus = () => {
    if (!lastRun) return { label: 'Never Run', color: 'muted', icon: Clock };
    if (lastRun.status === 'running') return { label: 'Running', color: 'primary', icon: Loader2, animate: true };
    if (lastRun.status === 'completed') return { label: 'Completed', color: 'emerald', icon: CheckCircle2 };
    if (lastRun.status === 'failed') return { label: 'Failed', color: 'destructive', icon: XCircle };
    return { label: 'Pending', color: 'amber', icon: Clock };
  };

  const status = getStatus();
  const StatusIcon = status.icon;

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Format future time
  const formatNextRun = (timestamp) => {
    if (!timestamp) return 'Not scheduled';
    if (!isScheduleEnabled) return 'Schedule paused';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = date - now;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 60) return `In ${diffMins}m`;
    if (diffHours < 24) return `In ${diffHours}h`;
    return date.toLocaleString();
  };

  const getStatusClasses = (color) => {
    const colors = {
      emerald: 'text-emerald-500',
      destructive: 'text-destructive',
      primary: 'text-primary',
      amber: 'text-amber-500',
      muted: 'text-muted-foreground',
    };
    return colors[color] || colors.muted;
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
          <Activity className="w-5 h-5" />
          Status
        </h3>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          Current automation status and run history overview.
        </p>
      </div>

      <div className="space-y-3">
        {/* Current Status */}
        <div className="flex items-center justify-between p-4 border border-zinc-700/50 rounded-lg bg-zinc-900 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]">
          <span className="text-sm font-medium text-foreground">Current Status</span>
          <div className={`flex items-center gap-2 ${getStatusClasses(status.color)}`}>
            <StatusIcon className={`w-4 h-4 ${status.animate ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium">{status.label}</span>
          </div>
        </div>

        {/* Last Run */}
        <div className="flex items-center justify-between p-4 border border-zinc-700/50 rounded-lg bg-zinc-900 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]">
          <span className="text-sm font-medium text-foreground">Last Run</span>
          <span className="text-sm text-muted-foreground">
            {formatRelativeTime(lastRun?.triggered_at)}
          </span>
        </div>

        {/* Next Run */}
        <div className="flex items-center justify-between p-4 border border-zinc-700/50 rounded-lg bg-zinc-900 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]">
          <span className="text-sm font-medium text-foreground">Next Run</span>
          <div className="flex items-center gap-2">
            {!isScheduleEnabled && (
              <Pause className="w-4 h-4 text-amber-500" />
            )}
            <span className={`text-sm ${isScheduleEnabled ? 'text-muted-foreground' : 'text-muted-foreground/60'}`}>
              {formatNextRun(nextRun)}
            </span>
          </div>
        </div>

        {/* Duration (if last run exists) */}
        {lastRun?.completed_at && lastRun?.triggered_at && (
          <div className="flex items-center justify-between p-4 border border-zinc-700/50 rounded-lg bg-zinc-900 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]">
            <span className="text-sm font-medium text-foreground">Last Duration</span>
            <span className="text-sm text-muted-foreground">
              {Math.round((new Date(lastRun.completed_at) - new Date(lastRun.triggered_at)) / 1000)}s
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusCard;
